import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { messaging } from "../services/messaging.js";

export function AddContact() {
  const nav = useNavigate();
  const [invite, setInvite] = useState<{ url: string } | null>(null);
  const [qr, setQr] = useState<string>("");
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void messaging
      .createInvite()
      .then(async (inv) => {
        if (cancelled) return;
        setInvite(inv);
        setQr(await QRCode.toDataURL(inv.url, { margin: 0, width: 400 }));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not create invite."));
    return () => {
      cancelled = true;
    };
  }, []);

  function extractToken(input: string): string | null {
    const trimmed = input.trim();
    const m = trimmed.match(/\/add\/([A-Za-z0-9_-]+)/);
    if (m) return m[1] ?? null;
    if (/^[A-Za-z0-9_-]{16,}$/.test(trimmed)) return trimmed;
    return null;
  }

  async function addToken(token: string) {
    setBusy(true);
    setError(null);
    try {
      const contact = await messaging.addContactByInvite(token);
      nav(`/chat/${contact.peerUserId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add contact.");
    } finally {
      setBusy(false);
    }
  }

  /** One tap: read the invite someone copied (e.g. from Safari) and add them. */
  async function pasteInvite() {
    setError(null);
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      setError("Couldn't read the clipboard — paste the link into the box below instead.");
      return;
    }
    const token = extractToken(text);
    if (!token) {
      setPaste(text);
      setError("The copied text isn't a TrueWave invite link.");
      return;
    }
    setPaste(text);
    await addToken(token);
  }

  /** The phone's own share sheet (WhatsApp, Messages, Mail...). */
  async function shareInvite() {
    if (!invite) return;
    try {
      await navigator.share({ title: "TrueWave invite", text: "Chat with me securely on TrueWave:", url: invite.url });
    } catch {
      /* dismissed */
    }
  }

  async function add() {
    const token = extractToken(paste);
    if (!token) {
      setError("That doesn't look like an invite link.");
      return;
    }
    await addToken(token);
  }

  return (
    <>
      <div className="topbar">
        <Link to="/"><button className="topbar-icon-btn" title="Back" aria-label="Back">←</button></Link>
        <strong className="grow">Add contact</strong>
      </div>

      <div className="center">
        <div className="card">
          <h1>Your invite</h1>
          <p>Show this QR code or send the link. Anyone with it can start a conversation with you.</p>
          {qr && <img className="qr" src={qr} alt="Your invite QR code" />}
          {invite && <div className="mono">{invite.url}</div>}
          {invite && (
            <div className="invite-actions">
              {typeof navigator.share === "function" && (
                <button className="primary" onClick={() => void shareInvite()}>
                  Share invite
                </button>
              )}
              <button onClick={() => void navigator.clipboard?.writeText(invite.url)}>Copy link</button>
            </div>
          )}
        </div>

        <div className="card">
          <h1>Add someone</h1>
          <p>Got an invite link? Copy it, then tap Paste invite.</p>
          <button className="primary" disabled={busy} onClick={() => void pasteInvite()}>
            📋 Paste invite
          </button>
          <p>Or paste it here:</p>
          <input
            value={paste}
            placeholder="https://…/#/add/…"
            onChange={(e) => setPaste(e.target.value)}
          />
          {error && <div className="error">{error}</div>}
          <button disabled={busy || !paste} onClick={add}>
            {busy ? "Adding…" : "Add contact"}
          </button>
        </div>
      </div>
    </>
  );
}
